import React, {useEffect, useRef, useState} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm';
import AnimatedDotsText from "@/commons/components/AnimatedDotsText";

import * as FunctionUtils from "./FunctionUtils";
import styles from './SupportComponent.module.scss';
import 'github-markdown-css/github-markdown-dark.css';
import ApiInterface from "@/commons/api/ApiInterface"; // ✔️ stile dark pronto
import CustomButton from "@/commons/components/buttons/customButton/CustomButton";

type Props = {
    fileName: string,
    requiredPermission: string
};

export default function SupportComponent({fileName, requiredPermission}: Props) {
    const [hasPermission, setHasPermission] = useState(true);
    const [working, setWorking] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const [supportContent, setSupportContent] = useState<string | null>(null);

    const [hasEditPermission, setHasEditPermission] = useState(false);

    const textareaRef = useRef<HTMLTextAreaElement>(null);

    function clearAll() {
        setWorking(false);
        setIsEditing(false);
        setSupportContent(null);
        setHasEditPermission(false);
    }

    // Caricamento iniziale del file
    useEffect(() => {
        clearAll();
        if (requiredPermission != "") {
            ApiInterface.hasPermission(requiredPermission).then(r => {
                if (r) {
                    setHasPermission(true);
                    loadPage()
                } else {
                    setHasPermission(false);
                }
            })
        } else {
            setHasPermission(true);
            loadPage()
        }
    }, [fileName]);

    let loadPage = () => {
        ApiInterface.hasPermission("admin.file.edit.override").then(r => setHasEditPermission(r));
        FunctionUtils.getFile(fileName).then((uuid) => {
            if (uuid == null) {
                createNewFile();
            } else {
                loadFile(uuid);
            }
        });
    }

    function loadFile(uuid: string) {
        FunctionUtils.getFileContent(uuid).then(text => {
            setSupportContent(text);
        });
    }

    function createNewFile() {
        setWorking(true);
        const content = [
            "### File mancante inizializzato",
            "Ora potrai modificare il tuo file come più ti pare"
        ].join("\n");
        setSupportContent(content);
        setWorking(false);
    }

    function handleCancel(){
        setIsEditing(false);
    }

    function handleSave() {
        if (!textareaRef.current) return;
        window.addPersistentInfo("warning", "Salvaggio in corso...","Il file " + fileName + " sta venendo salvato...");
        const text = textareaRef.current.value;
        setSupportContent(text); // aggiorna per la preview

        const lines = text.split(/\r?\n/);
        const file = new File([lines.join("\n")], fileName, {type: "text/markdown"});

        setWorking(true);
        FunctionUtils.uploadFile(file, fileName)
            .then(() => {
                window.updatePersistentInfo({
                    type: "success",
                    title: "File salvato",
                    text: "Il file " + fileName + " è stato salvato correttamente!",
                    destroyIn: 5
                });
            })
            .catch(() => {
                window.updatePersistentInfo({
                    type: "alert",
                    title: "Errore",
                    text: "Salvataggio fallito!",
                    destroyIn: 5
                });
            })
            .finally(() => setWorking(false));

        setIsEditing(false);
    }

    return (
        <>
            {hasPermission ? (
                <>
                    {supportContent === null ? (
                        <div className="markdown-body">
                            <div className={styles.markdownBody}>
                                <h1><AnimatedDotsText text="Caricamento del file in corso"/></h1>
                            </div>
                        </div>
                    ) : isEditing ? (
                        <>
                    <textarea
                        className={styles.textarea}
                        ref={textareaRef}
                        defaultValue={supportContent}
                        rows={50}
                    />
                            <CustomButton disabled={working} onClick={handleCancel}>
                                Annulla
                            </CustomButton>
                            <CustomButton disabled={working} onClick={handleSave}>
                                {working ? "Salvataggio in corso..." : "Salva"}
                            </CustomButton>
                        </>
                    ) : (
                        <>
                            <div className="markdown-body">
                                <div className={styles.markdownBody}>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {supportContent}
                                    </ReactMarkdown>
                                </div>
                            </div>
                            {hasEditPermission && (
                                <CustomButton disabled={working} onClick={() => setIsEditing(true)}>
                                    {working ? "Caricamento..." : "Modifica"}
                                </CustomButton>
                            )}
                        </>
                    )}</>
            ) : (
                <>
                    <div className="markdown-body">
                        <div className={styles.markdownBody}>
                            <h1>Non hai il permesso per vedere questo file!</h1>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}